/**
 * Sponsor logo display rules for the fan page.
 *
 * Clubs keep a list of sponsor logos (club_settings.sponsor_logos jsonb) and
 * a display size (sponsor_logo_size, percent of the 40px base). The list can
 * be absent or empty on rows that predate the migration, and the legacy
 * single column sponsor_logo_url is still the source for those — and stays
 * the mirror of the first list entry, because receipts_view reads it as the
 * club logo.
 *
 * Kept out of TagPage so the fallback/clamping rules are testable without
 * rendering the fan page — same reasoning as orderNotice.ts and fanTheme.ts.
 */

export const SPONSOR_LOGO_BASE_HEIGHT_PX = 40;
export const SPONSOR_LOGO_MIN_PCT = 50;
export const SPONSOR_LOGO_MAX_PCT = 200;
export const SPONSOR_LOGO_DEFAULT_PCT = 100;

/** Ticker pace: the loop takes this many seconds per logo, so the drift
 *  speed stays constant regardless of how many sponsors a club has. */
export const SPONSOR_TICKER_SECONDS_PER_LOGO = 6;

/**
 * Pixel max-height for a sponsor <img>: base × size/100. A missing column
 * (pre-migration row) or garbage value renders at the default, and
 * out-of-band DB values clamp to the slider bounds so a bad row can never
 * blow up the layout.
 */
export function sponsorLogoMaxHeight(
  size: number | null | undefined,
  base: number = SPONSOR_LOGO_BASE_HEIGHT_PX,
): number {
  const pct = typeof size === 'number' && Number.isFinite(size) ? size : SPONSOR_LOGO_DEFAULT_PCT;
  const clamped = Math.min(SPONSOR_LOGO_MAX_PCT, Math.max(SPONSOR_LOGO_MIN_PCT, pct));
  return Math.round((base * clamped) / 100);
}

/**
 * The logo URLs the fan page should show, in display order. Malformed list
 * entries are dropped rather than rendered as broken images; an empty or
 * absent list falls back to the legacy single column.
 */
export function resolveSponsorLogos(
  settings:
    | { sponsor_logos?: unknown; sponsor_logo_url?: string | null }
    | null
    | undefined,
): string[] {
  if (!settings) return [];

  const raw = settings.sponsor_logos;
  if (Array.isArray(raw)) {
    const urls = raw
      .map(entry =>
        entry && typeof entry === 'object' && typeof (entry as { url?: unknown }).url === 'string'
          ? ((entry as { url: string }).url).trim()
          : '',
      )
      .filter(url => url !== '');
    if (urls.length > 0) return urls;
  }

  const legacy = (settings.sponsor_logo_url ?? '').trim();
  return legacy ? [legacy] : [];
}
